import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Typography } from '@mui/material';
import { fetchAttachmentBlob } from '../../api/hooks/useScopeAttachments';

const ATTACHMENT_PREFIX = 'attachment:';

/** Lädt ein Anhang-Bild authentifiziert als Blob (die Route verlangt den Auth-Header). */
function AttachmentImage({ projectId, id, alt }: { projectId: string; id: string; alt?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    fetchAttachmentBlob(projectId, id)
      .then((blob) => { if (!cancelled) { url = URL.createObjectURL(blob); setSrc(url); } })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [projectId, id]);
  if (failed) return <Typography component="span" variant="caption" color="error">{alt ?? id}</Typography>;
  if (!src) return null;
  return <img src={src} alt={alt ?? ''} style={{ maxWidth: '100%' }} />;
}

/** Markdown-Vorschau des Scope Statements; `![..](attachment:<id>)` zeigt das hochgeladene Bild. */
export function ScopePreview({ projectId, value }: { projectId: string; value: string }) {
  return (
    <Box data-testid="scope-preview" sx={{ '& table': { borderCollapse: 'collapse' }, '& td, & th': { border: '1px solid var(--pm-bg-muted)', px: 1 } }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        // react-markdown verwirft unbekannte Protokolle; `attachment:` ist unser eigenes und muss durch.
        urlTransform={(url) => (url.startsWith(ATTACHMENT_PREFIX) ? url : /^(https?:|mailto:|#|\/)/i.test(url) ? url : '')}
        components={{
          img: ({ src, alt }) =>
            typeof src === 'string' && src.startsWith(ATTACHMENT_PREFIX)
              ? <AttachmentImage projectId={projectId} id={src.slice(ATTACHMENT_PREFIX.length)} alt={alt} />
              : <img src={src} alt={alt ?? ''} style={{ maxWidth: '100%' }} />,
        }}
      >
        {value}
      </ReactMarkdown>
    </Box>
  );
}
